import { useState } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  DocumentDuplicateIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableRow({ id, item, columns, allowHTML = false, onView, onEdit, onDelete, onDuplicate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-slate-50 transition-colors"
    >
      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400 cursor-grab active:cursor-grabbing">
        <div {...attributes} {...listeners}>
          <Bars3Icon className="h-5 w-5" />
        </div>
      </td>
      {columns.map((col, index) => {
        const toRender = col.render ? col.render(item[col.key], item) : item[col.key]
        console.log('typeof', typeof toRender)
        return allowHTML && typeof toRender === 'string' ?
          (<td key={`${col.key}_${index}`} className="px-6 py-4 whitespace-nowrap text-sm text-slate-900" dangerouslySetInnerHTML={{ __html: toRender }} />)
          :
          (<td key={`${col.key}_${index}`} className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
            {toRender}
          </td>)
      })}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
        {onView && (
          <button
            onClick={() => onView(item)}
            className="inline-flex items-center px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View
          </button>
        )}
        {onDuplicate && (
          <button
            onClick={() => onDuplicate(item)}
            className="inline-flex items-center px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md transition-colors"
          >
            <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
            Duplicate
          </button>
        )}
        {onEdit && (
          <button
            onClick={() => onEdit(item)}
            className="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(item)}
            className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}

export default function DataTable({
  data,
  columns,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  onAdd,
  renderFilters,
  reorderable = false,
  allowHTML = false,
  onReorder,
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onReorder) {
      const oldIndex = data.findIndex((item) => item._id === active.id);
      const newIndex = data.findIndex((item) => item._id === over.id);

      const reorderedData = arrayMove(data, oldIndex, newIndex);

      // Calculate affected items range
      const minIndex = Math.min(oldIndex, newIndex);
      const maxIndex = Math.max(oldIndex, newIndex);
      const affectedItems = reorderedData
        .slice(minIndex, maxIndex + 1)
        .map((item, idx) => ({
          id: item._id,
          displayOrder: minIndex + idx,
        }));

      onReorder(affectedItems);
    }
  };

  const getSortedData = () => {
    // Disable sorting when reorderable mode is active
    if (reorderable) return data;
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = getSortedData();
  // Disable pagination when reorderable mode is active
  const totalPages = reorderable ? 1 : Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = reorderable ? sortedData : sortedData.slice(startIndex, startIndex + itemsPerPage);

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUpIcon className="h-4 w-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-blue-600" />
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {columns[0]?.title || 'Data'} Management
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {sortedData.length} {sortedData.length === 1 ? 'item' : 'items'} total
            </p>
          </div>
          {onAdd && (
            <button
              onClick={onAdd}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all hover:shadow-md"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New
            </button>
          )}
        </div>

        {/* Filters */}
        {renderFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            {renderFilters()}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {reorderable && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-12">
                      Order
                    </th>
                  )}
                  {columns.map((col, index) => (
                    <th
                      key={`${col.key}_${index}`}
                      onClick={() => !reorderable && col.sortable !== false && handleSort(col.key)}
                      className={`px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider ${!reorderable && col.sortable !== false
                        ? 'cursor-pointer hover:bg-slate-100 select-none'
                        : ''
                        }`}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{col.label}</span>
                        {!reorderable && col.sortable !== false && getSortIndicator(col.key)}
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {paginatedData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + (reorderable ? 2 : 1)}
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      <div className="flex flex-col items-center">
                        <svg className="h-12 w-12 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-sm">No data found</p>
                      </div>
                    </td>
                  </tr>
                ) : reorderable ? (
                  <SortableContext
                    items={paginatedData.map(item => item._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {paginatedData.map((item) => (
                      <SortableRow
                        key={item._id}
                        id={item._id}
                        item={item}
                        columns={columns}
                        allowHTML={allowHTML}
                        onView={onView}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                      />
                    ))}
                  </SortableContext>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr
                      key={item._id || item.id || index}
                      className="hover:bg-slate-50 transition-colors"
                    >

                      {columns.map((col, indexCol) => {
                        const toRender = col.render ? col.render(item[col.key], item) : item[col.key]
                        console.log('typeof', typeof toRender)

                        return allowHTML && typeof toRender === 'string' ?
                          (<td key={`${col.key}_cell_${index}_${indexCol}`} className="px-6 py-4 whitespace-nowrap text-sm text-slate-900" dangerouslySetInnerHTML={{ __html: col.render ? col.render(item[col.key], item) : item[col.key] }} />)
                          :
                          (<td key={`${col.key}_cell_${index}_${indexCol}`} className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {col.render ? col.render(item[col.key], item) : item[col.key]}
                          </td>)
                      })}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        {onView && (
                          <button
                            onClick={() => onView(item)}
                            className="inline-flex items-center px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View
                          </button>
                        )}
                        {onDuplicate && (
                          <button
                            onClick={() => onDuplicate(item)}
                            className="inline-flex items-center px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md transition-colors"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                            Duplicate
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => onEdit(item)}
                            className="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(item)}
                            className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DndContext>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 flex items-center justify-between">
            <div className="text-sm text-slate-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(startIndex + itemsPerPage, sortedData.length)}
              </span>{' '}
              of <span className="font-medium">{sortedData.length}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>
              <span className="text-sm text-slate-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
